import { PRIMITIVE, px } from "@/tokens/design-tokens";
import {
  PsychiatricProgressStateExam,
  TreatmentPlan,
} from "../../../../../../scheduling/appointment/encounter/encounter-constant";

/**
 * SOAP band headings shown above the note fields in the Psychiatric Progress
 * Exam form. Same wording as the Encounter Summary Report bands so
 * the encounter screen and the print read the same top to bottom.
 */
export const PSYCH_PROGRESS_SOAP_HEADING = {
  SUBJECTIVE: "Subjective",
  OBJECTIVE: "Objective",
  ASSESSMENT: "Assessment",
  PLAN: "Plan",
} as const;

/**
 * Sub-headings that group fields inside a SOAP band. Rendered in the same bold
 * dark-grey the preview headers above them use (e.g. "ROS Templates"), so a
 * sub-heading never reads as a SOAP band — those stay blue.
 */
export const PSYCH_PROGRESS_SUBHEADING = {
  MENTAL_STATUS_EXAM: "Mental Status Examination (MSE)",
} as const;

export const PSYCH_PROGRESS_SUBHEADING_SX = {
  fontWeight: "bold",
  color: "#424242",
} as const;

/**
 * Row wrapper for a lone IcdCodesField / CptCodesField.
 *
 * Those fields stack their add-button and their selected-codes list in a
 * column, and a column stretches its children — so when the field is the only
 * item in its row the button spans the full width instead of reading as a
 * button. Pinning the column to flex-start gives the button its own width; the
 * selected-codes list sets `width: 100%` on itself, so it still fills the row.
 */
export const CODE_FIELD_ROW_SX = {
  "& > div": { alignItems: "flex-start" },
} as const;

export const PSYCH_PROGRESS_EXAM_FIELDS: {
  value: keyof PsychiatricProgressStateExam;
  label: string;
}[] = [
  { value: "appearance", label: "Apperance" },
  { value: "behavior", label: "Behavior" },
  { value: "speech", label: "Speech" },
  { value: "mood", label: "Mood" },
  { value: "affect", label: "Affect" },
  { value: "thoughtProcess", label: "Thought Process & Content" },
  { value: "perceptions", label: "Perceptions" },
  { value: "cognition", label: "Cognition" },
];

export const PSYCH_PROGRESS_EXAM_PLAN_FIELDS: {
  value: keyof TreatmentPlan;
  label: string;
}[] = [
  { value: "treatment", label: "Plan" },
  {
    value: "futureConsiderations",
    label: "Future Considerations",
  },
  { value: "labStudy", label: "Lab Study" },
  { value: "referrals", label: "Referrals" },
  { value: "collateral", label: "Collateral" },
  { value: "safety", label: "Safety" },
];

/**
 * Split "Start Scribe" control in the visit-note toolbar.
 * The accent is the same live-scribe pink the scribe window uses.
 */
export const START_SCRIBE_BUTTON = {
  LABEL: "Start Scribe",
  MENU_ARIA_LABEL: "More scribe options",
  ACCENT: PRIMITIVE.color.pink[500],
  ACCENT_HOVER_BG: PRIMITIVE.color.pink["00"],
  RADIUS: px(PRIMITIVE.radius[4]),
  CARET_WIDTH: px(PRIMITIVE.spacing[24]),
  LABEL_PX: px(PRIMITIVE.spacing[8]),
  LABEL_GAP: px(PRIMITIVE.spacing[4]),
} as const;

export const START_SCRIBE_MENU_ORIGIN = {
  anchor: { vertical: "bottom", horizontal: "right" },
  transform: { vertical: "top", horizontal: "right" },
} as const;

/** DOM id of the slot at the end of the visit-information row that notes portal their header actions into. */
export const NOTE_HEADER_ACTIONS_ID = "note-header-actions";
/** Row under the visit information where a note form mounts its required-sections summary. */
export const NOTE_REQUIRED_SUMMARY_ID = "note-required-summary";

// Vite build modes; the `sandbox` build is what serves stage.
const SCRIBE_HIDDEN_ON_PREMIER_NOTES_MODES: readonly string[] = [
  // "development",
  "qa",
  "sandbox",
  "prod",
];
export const IS_SCRIBE_HIDDEN_ON_PREMIER_NOTES =
  SCRIBE_HIDDEN_ON_PREMIER_NOTES_MODES.includes(import.meta.env.MODE);
